import React, { useState, useRef, useEffect } from 'react';
import { palette } from '../../themes/theme';
import * as Sentry from '@sentry/react';
import ReactPlayer from 'react-player';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../redux/hooks';
import { selectLayout } from '../../redux/selectors';
import { useTranslation } from 'react-i18next';
import { trackVideoPlayed, trackVideoFinished } from '../../mixpanel';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import CloseIcon from '@mui/icons-material/Close';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import { createImageUrl } from '../../services/data';
import { isMediaPlayAbortError, describeMediaError } from '../../utils/errorHandler';
import ProgressBar from './ProgressBar';
import Controls from './Controls';
import { formatTime } from '../../services/formatFunctions';
import useScreenOrientation from '../../hooks/useOrientation'
import { useFullscreen } from '../../hooks/useFullscreen'
import OrientationPrompt from '../common/OrientationPrompt';
import styles from './Player.module.css';
import '../../styles/common/animations.css'

import RoundPlay from '../../assets/roundPlay.svg?react'
import RoundPause from '../../assets/roundPause.svg?react' 
import logoSvg from '../../assets/Xsite.svg';
import IconButton from '@mui/material/IconButton';
import { HE_LOCALE } from '../../internationalization/internationalization';

interface VideoPlayerProps {
  video: string;
  name?: string;
  withOverlay?: boolean;
  withHorizontal?: boolean;
  onVideoEnd?: () => void;
  autoplay?: boolean;
  withClose?: boolean;
  aspectRatio?: string;
  poster?: string;
  subtitles?: string;
  simpleControls?: boolean;
}

const VideoPlayer: React.FC<VideoPlayerProps> = ({video, name, withOverlay, withHorizontal, onVideoEnd, autoplay, withClose, aspectRatio, poster, subtitles, simpleControls}) => {
    const [isPlaying, setIsPlaying] = useState<boolean>(false)
    const [duration, setDuration] = useState<number>(0)
    const [currentTime, setCurrentTime] = useState<number>(0)
    const [isLoading, setIsLoading] = useState<boolean>(true)
    const [hasError, setHasError] = useState<boolean>(false)
    const [showControl, setShowControls] = useState<boolean>(true)
    const [showOrientationPrompt, setShowOrientationPrompt] = useState<boolean>(false)
    const [hasShownPrompt, setHasShownPrompt] = useState<boolean>(false)
    const navigate = useNavigate()
    const { t } = useTranslation()
    
    const layout = useAppSelector(selectLayout);
    
    const videoRef = useRef<ReactPlayer | null>(null)
    const videoElementRef = useRef<HTMLVideoElement | null>(null)

    
    
    const playerContainerRef = useRef<HTMLDivElement>(null)
    const playerWrapperRef = useRef<HTMLDivElement>(null)

    // Release the <video> when the station unmounts. react-player only removes
    // `src`, which per the HTML spec does not abort a load or free the media
    // pipeline. iOS Safari has a small budget of media players. Paging quickly
    // through a dozen stations exhausted it, and every later video then failed
    // at once with MEDIA_ERR_DECODE / SRC_NOT_SUPPORTED (Sentry CUSTOM-APP-2H).
    // The wrapper is read at mount: by cleanup time the refs are detached, but
    // the detached subtree still holds the element.
    useEffect(() => {
        const wrapper = playerWrapperRef.current
        return () => {
            const media = wrapper?.querySelector('video')
            if (!media) return
            media.pause()
            media.removeAttribute('src')
            media.load()
        }
    }, [])
    const orientation = useScreenOrientation()
    const { isFullscreen: isFullScreen, toggle: toggleFullScreen } = useFullscreen(playerContainerRef, videoElementRef);

    const handleOrientationPromptDismiss = () => {
        setShowOrientationPrompt(false);
        setHasShownPrompt(true);
        setIsPlaying(true);
    };


  const isLandscape = orientation === 'landscape';


    const handlePlayerReady = () => {
        videoElementRef.current = videoRef.current?.getInternalPlayer() as HTMLVideoElement
        setCurrentTime(0)
        setIsLoading(false)

            if (autoplay && !(layout === 'walking' && orientation === 'portrait' && !hasShownPrompt)) {
              setIsPlaying(true)
              setShowControls(false)
              trackVideoPlayed(name ?? video);
            }
    }

    const handleError = (err: unknown) => {
        // A play() request interrupted by pause()/unmount is a benign race —
        // don't report it or show the error overlay; the player will reconcile.
        if (isMediaPlayAbortError(err)) return;

        const isPlayBlocked =
            err instanceof DOMException &&
            (err.name === 'NotSupportedError' || err.name === 'NotAllowedError');
        // A <video> error Event is reported as a MediaPlaybackError (code + src)
        // so Sentry can tell a dead file from a lost connection; an abandoned
        // load (MEDIA_ERR_ABORTED) describes as null and is not reported.
        const reportable = isPlayBlocked ? null : describeMediaError(err);
        if (reportable !== null) {
            Sentry.captureException(reportable);
        }
        setHasError(true);
        setIsLoading(false);
        setIsPlaying(false);
    }

    const handleSeek = (time: number) => {
        setCurrentTime(time)
        videoRef.current?.seekTo(time)
    }

    const togglePlayPause = (e: React.MouseEvent) => {
        e.stopPropagation();
        
        if (!isPlaying) {
            if (layout === 'walking' && orientation === 'portrait' && !hasShownPrompt) {
                setShowOrientationPrompt(true);
                return;
            }
            setIsPlaying(true);
            setShowControls(false);
            trackVideoPlayed(name ?? video);
        } else {
            setIsPlaying(false);
        }
    };

    const handleVideoClick = () => {
        setShowControls(true);        
        setTimeout(() => setShowControls(false), 3000);
    };

    const handleVideoEnd = () => {
        trackVideoFinished(name ?? video);
        onVideoEnd && onVideoEnd()
    }

    const handleFullscreenClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        toggleFullScreen();
    };

    return (
        <div className={styles.videoContainer} ref={playerContainerRef} style={{padding: isLandscape? '0 2rem' : '', aspectRatio: aspectRatio}}>
          {
            withClose && !isLoading && 
              <IconButton aria-label='close' disableFocusRipple={true} disableRipple={true} onClick={() => navigate(-1)} size='large' sx={{display: {xs: 'none', sm: 'initial'}, position: 'absolute', zIndex: 1000, top: '5%', left: '95%', padding: '5px 8px', backgroundColor: palette.surface.overlayLight}}>
                <CloseIcon  />
              </IconButton>
          }
            <div className={styles.playerWrapper} ref={playerWrapperRef} onClick={handleVideoClick} style={{ marginTop: isLandscape? '3%' : ''}}>
                {
                    hasError &&
                    <div className={styles.loaderOverlay} style={{ backgroundColor: 'rgba(0,0,0,0.7)' }}>
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                            <ErrorOutlineIcon sx={{ color: 'white', fontSize: 40 }} />
                            <Typography sx={{ color: 'white', fontFamily: 'Poppins, sans-serif', fontSize: '14px' }}>
                                {t('common.video-unavailable')}
                            </Typography>
                        </Box>
                    </div>
                }
                {
                    isLoading && !hasError &&
                    <div className={styles.loaderOverlay}>
                        <img src={logoSvg} alt='asite-logo' className='spin-pulse' />
                    </div>
                }
                {
                    !simpleControls && withOverlay && showControl &&
                    <button className={styles.overlay} onClick={e => togglePlayPause(e)}>
                      
                        {isPlaying? <RoundPause /> : <RoundPlay />}
                    </button>
                }
                {
                    withOverlay && showControl && withHorizontal &&
                    <Box 
                        component="button"
                        className='overlay-fullscreen' 
                        onClick={handleFullscreenClick}
                        sx={{
                            position: 'absolute',
                            top: '20px',
                            right: '20px',
                            zIndex: 200,
                            backgroundColor: 'black',
                            border: 'none',
                            borderRadius: '50%',
                            width: '40px',
                            height: '40px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer'
                        }}
                    >
                        {isFullScreen ? 
                            <FullscreenExitIcon sx={{ color: 'white', fontSize: '20px' }} /> : 
                            <FullscreenIcon sx={{ color: 'white', fontSize: '20px' }} />
                        }
                    </Box>
                }
                <ReactPlayer
                    ref={videoRef}
                    url={createImageUrl(video)}
                    width='100%'
                    height='100%'
                    playing={isPlaying}
                    onDuration={(duration: number) => setDuration(duration)}
                    onProgress={(state: { playedSeconds: number }) => setCurrentTime(state.playedSeconds)}
                    onError={handleError}
                    onReady={handlePlayerReady}
                    onEnded={handleVideoEnd}
                    muted={autoplay}
                    config={{
                      file: {
                        attributes: {
                          // react-player defaults to preload="auto", which downloads the
                          // whole file (12-30MB) the moment a station opens, before any
                          // tap. Metadata is enough to show the duration; the rest
                          // streams on play.
                          preload: 'metadata',
                          ...(subtitles ? { crossOrigin: 'anonymous' } : {}),
                          poster: poster ? createImageUrl(poster) : undefined
                        },
                        tracks: subtitles ? [
                          {
                              kind: 'subtitles' as const,
                              src: createImageUrl(subtitles),
                              srcLang: HE_LOCALE,
                              label: 'Hebrew',
                              default: true,
                          }
                        ] : []
                      }
                    }}
                />
            </div>
            <ProgressBar
                playerRef={videoElementRef}
                progress={currentTime}
                totalDuration={duration}
                onSeek={handleSeek}
            />
            {simpleControls ? (
              <>
                <div style={{display: 'flex', justifyContent: 'space-between'}}>
                    <p>{formatTime(currentTime)}</p>
                    <p>{formatTime(duration)}</p>
                </div>
                {/* notranslate: transport controls — Google Translate's DOM edits
                    break React reconciliation on iOS (Sentry CUSTOM-APP-D). */}
                <div className='notranslate' translate='no' style={{textAlign: 'center'}}>
                    <button
                        disabled={duration === 0}
                        style={{height: '8vh', width: '11vh', backgroundColor: 'transparent', border: 'none', cursor: 'pointer', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.3))'}}
                        onClick={() => {
                            if (!isPlaying) {
                                setIsPlaying(true);
                                trackVideoPlayed(name ?? video);
                            } else {
                                setIsPlaying(false);
                            }
                        }}
                    >
                        {isPlaying ? <RoundPause aria-label='pause' width='100%' height='100%'/> : <RoundPlay aria-label='play' width='100%' height='100%'/>}
                    </button>
                </div>
              </>
            ) : (
              <Controls
                  playerRef={videoElementRef}
                  progress={currentTime}
                  setIsPlaying={setIsPlaying}
                  isFullScreen={isFullScreen}
                  totalDuration={duration}
                  toggleFullScreen={withHorizontal? toggleFullScreen : undefined}
              />
            )}
            
            <OrientationPrompt 
                showPrompt={showOrientationPrompt}
                onPromptDismiss={handleOrientationPromptDismiss}
            />
        </div>
    );
};

export default VideoPlayer;
